import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { messageService } from '../../services/messageService';
import MessageDetail from '../../components/messages/MessageDetail';
import MessageList from '../../components/messages/MessageList';
import MessageComposer from '../../components/messages/MessageComposer';
import { Search, Filter, Edit, Send } from 'lucide-react';

const MessagesPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState({
        type: '',  // 可以是 'system', 'personal', 'broadcast'
        sender: '',
        read_status: ''
    });
    const [showComposer, setShowComposer] = useState(false);

    useEffect(() => {
        fetchMessages();
    }, [filters]);

    const fetchMessages = async () => {
        setIsLoading(true);
        try {
            const { data } = await messageService.getMessages(filters);
            setMessages(data);
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMessageSelect = (message) => {
        setSelectedMessage(message);
        // 標記為已讀
        messageService.markAsRead(message.id);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleResetFilters = () => {
        setFilters({
            type: '',
            sender: '',
            read_status: ''
        });
    };

    const handleNewMessage = async (messageData) => {
        try {
            await messageService.sendMessage(messageData);
            fetchMessages();
            setShowComposer(false);
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">訊息中心</h1>
                <button
                    onClick={() => setShowComposer(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center"
                >
                    <Edit className="w-5 h-5 mr-2" />
                    新增訊息
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 左側篩選與訊息列表 */}
                <div className="lg:col-span-1 bg-white rounded-lg shadow-sm">
                    <div className="p-4 border-b border-gray-200">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    name="sender"
                                    value={filters.sender}
                                    onChange={handleFilterChange}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                                    placeholder="搜尋發送者..."
                                />
                            </div>

                            <select
                                name="type"
                                value={filters.type}
                                onChange={handleFilterChange}
                                className="border border-gray-300 rounded-md px-3 py-2"
                            >
                                <option value="">所有類型</option>
                                <option value="system">系統訊息</option>
                                <option value="personal">個人訊息</option>
                                <option value="broadcast">廣播訊息</option>
                            </select>

                            <select
                                name="read_status"
                                value={filters.read_status}
                                onChange={handleFilterChange}
                                className="border border-gray-300 rounded-md px-3 py-2"
                            >
                                <option value="">所有狀態</option>
                                <option value="unread">未讀</option>
                                <option value="read">已讀</option>
                            </select>

                            {(filters.type || filters.sender || filters.read_status) && (
                                <button
                                    onClick={handleResetFilters}
                                    className="text-blue-500 hover:underline text-sm"
                                >
                                    重置篩選
                                </button>
                            )}
                        </div>
                    </div>

                    <MessageList
                        messages={messages}
                        loading={isLoading}
                        onMessageSelect={handleMessageSelect}
                        selectedMessageId={selectedMessage?.id}
                    />
                </div>

                {/* 右側訊息詳情 */}
                <div className="lg:col-span-2">
                    {selectedMessage ? (
                        <MessageDetail
                            message={selectedMessage}
                            onReply={() => {
                                setShowComposer(true);
                                // 預先填入回覆資訊
                            }}
                        />
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                            <Send className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">選擇一則訊息以檢視詳情</p>
                        </div>
                    )}
                </div>
            </div>

            {/* 新增/回覆訊息彈窗 */}
            {showComposer && (
                <MessageComposer
                    initialData={selectedMessage ? {
                        recipient_id: selectedMessage.sender_id,
                        recipient_name: selectedMessage.sender_name,
                        subject: `Re: ${selectedMessage.subject}`
                    } : null}
                    onSend={handleNewMessage}
                    onCancel={() => setShowComposer(false)}
                />
            )}
        </div>
    );
};

export default MessagesPage;